import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import NotificationBell from "@/components/NotificationBell";
import { Home, Calendar, Activity, HeartPulse, MessageCircle, Lightbulb, User, LogOut, ShieldCheck, Gift, MoreHorizontal, LineChart } from "lucide-react";

const items = [
  { to: "/app/dashboard", label: "Home", icon: Home, testid: "nav-home" },
  { to: "/app/training", label: "Training", icon: Activity, testid: "nav-training" },
  { to: "/app/calendar", label: "Calendar", icon: Calendar, testid: "nav-calendar" },
  { to: "/app/messages", label: "Coach", icon: MessageCircle, testid: "nav-messages" },
  { to: "/app/progress", label: "Progress", icon: LineChart, testid: "nav-progress" },
  { to: "/app/recovery", label: "Recovery", icon: HeartPulse, testid: "nav-recovery" },
  { to: "/app/profile", label: "Profile", icon: User, testid: "nav-profile" },
  { to: "/app/referrals", label: "Refer", icon: Gift, testid: "nav-referrals" },
  { to: "/app/tips", label: "Tips", icon: Lightbulb, testid: "nav-tips" },
];

// Mobile bottom nav: 4 primary + More overflow with the rest
const mobilePrimary = items.slice(0, 4);
const mobileOverflow = items.slice(4);

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#070707] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#070707]/95 backdrop-blur border-b border-[#1f1f1f]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate("/app/dashboard")} className="flex items-center gap-2" data-testid="header-logo">
            <span className="font-display text-2xl tracking-wider">COACH<span className="text-[#d7ff00]">JP</span></span>
          </button>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <button onClick={() => navigate("/admin")} className="hidden sm:flex items-center gap-1 text-xs font-mono-jp uppercase tracking-widest text-[#d7ff00] border border-[#d7ff00]/40 px-3 py-1.5 hover:bg-[#d7ff00]/10" data-testid="admin-link">
                <ShieldCheck className="w-3.5 h-3.5" /> ADMIN
              </button>
            )}
            <button onClick={logout} className="text-[#888] hover:text-white p-2" data-testid="logout-btn" aria-label="Logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Desktop secondary nav */}
        <nav className="hidden md:block border-t border-[#1f1f1f]">
          <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto hide-scrollbar">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} data-testid={it.testid}
                className={({ isActive }) => `px-4 py-3 text-xs font-mono-jp uppercase tracking-widest ${isActive ? "text-[#d7ff00] border-b-2 border-[#d7ff00]" : "text-[#888] hover:text-white"}`}>
                {it.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      {/* Page */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-28 md:pb-10 fade-in">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0a0a0a]/95 backdrop-blur border-t border-[#1f1f1f]">
        <div className="grid grid-cols-5 max-w-6xl mx-auto">
          {mobilePrimary.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink key={it.to} to={it.to} data-testid={`m-${it.testid}`}
                className={({ isActive }) => `flex flex-col items-center justify-center py-2.5 text-[10px] font-mono-jp uppercase tracking-widest ${isActive ? "text-[#d7ff00]" : "text-[#888]"}`}>
                <Icon className="w-5 h-5 mb-1" /> {it.label}
              </NavLink>
            );
          })}
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <button className="flex flex-col items-center justify-center py-2.5 text-[10px] font-mono-jp uppercase tracking-widest text-[#888] hover:text-white" data-testid="m-nav-more">
                <MoreHorizontal className="w-5 h-5 mb-1" /> More
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-[#0a0a0a] border-t border-[#d7ff00]/30 text-white">
              <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] mb-4">MORE</div>
              <div className="grid grid-cols-3 gap-3">
                {mobileOverflow.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink key={it.to} to={it.to} onClick={() => setSheetOpen(false)} data-testid={`m-more-${it.testid}`}
                      className={({ isActive }) => `flex flex-col items-center justify-center py-4 border ${isActive ? "border-[#d7ff00] text-[#d7ff00]" : "border-[#1f1f1f] text-white"}`}>
                      <Icon className="w-5 h-5 mb-2" />
                      <span className="font-mono-jp text-[10px] uppercase tracking-widest">{it.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </div>
  );
}
